<template>
  <div
    ref="tableRoot"
    class="relative"
    :class="bulkEditColumnClass"
    :data-testid="`datatable-${dataKey}`"
  >
    <Toolbar
      v-if="
        showPrimaryToolbar &&
        rowsPerPageOptions[0] &&
        !!(quickFilteredData.length > rowsPerPageOptions[0] || props.showRowActions)
      "
      :class="['mb-8', 'p-2', 'py-1']"
      className="primaryToolbar"
    >
      <template #start>
        <slot name="custom-primaryStart"></slot>
        <Paginator
          v-if="quickFilteredData.length > rowsPerPageOptions[0] || settingsDrawerVisible"
          :first="currentPage * pageSize"
          :rows="pageSize"
          :pageLinkSize="3"
          :totalRecords="quickFilteredData.length"
          :rowsPerPageOptions="rowsPerPageOptions"
          :class="[quickFilteredData.length / pageSize <= 1 && 'singlepage']"
          @page="handlePaginatorChange"
        />
      </template>
      <template #center></template>
      <template #end>
        <slot name="custom-primaryActions">
          <Button
            v-if="props.showRowActions"
            icon="pi pi-plus"
            :label="addLabel"
            :disabled="!profileStore.userProfile?.isSeyboldUser"
            @click="handleCreateClick"
          />
        </slot>
      </template>
    </Toolbar>

    <div v-if="processedData?.length">
      <Toolbar
        :class="!props.showSecondaryToolbar ? 'hidden' : 'mb-2 px-0'"
        className="secondaryToolbar"
      >
        <template #start>
          <DataTablePrefilterTabs
            v-if="prefilterTabs?.length"
            :tabs="prefilterTabs"
            :focusedColumns="focusedColumns"
            :data-key="dataKey"
            @change="onPrefilterChange"
          />

          <slot
            name="custom-secondaryActions"
            :selectedRows="selectedRowsState"
            :confirmDeleteSelected="confirmDeleteSelected"
            :handleSave="handleSave"
          >
            <ButtonGroup
              v-if="
                props.onSave ||
                (props.showRowActions &&
                  selectedRowsState.length &&
                  props.onDelete &&
                  props.showDelete)
              "
              class="ml-4"
            >
              <Button
                v-if="props.onSave"
                v-tooltip="{
                  value: $t('buttons.save'),
                  fitContent: true,
                  showDelay: 300,
                }"
                icon="pi pi-save"
                :label="
                  props.showSecondaryToolbarButtons
                    ? undefined
                    : viewportStore.isNarrow
                      ? undefined
                      : $t('buttons.save')
                "
                :disabled="!profileStore.userProfile?.isSeyboldUser"
                @click="handleSave"
              />
              <Button
                v-if="
                  props.onDelete &&
                  props.showRowActions &&
                  selectedRowsState.length &&
                  props.showDelete
                "
                v-tooltip="{
                  value: $t('labels.deleteMany', { count: selectedRowsState.length }),
                  fitContent: true,
                  showDelay: 300,
                }"
                icon="pi pi-trash"
                :label="
                  props.showSecondaryToolbarButtons
                    ? undefined
                    : viewportStore.isNarrow
                      ? undefined
                      : $t('labels.deleteMany', { count: selectedRowsState.length })
                "
                severity="danger"
                :disabled="!profileStore.userProfile?.isSeyboldUser"
                @click="confirmDeleteSelected"
              />
            </ButtonGroup>
          </slot>

          <ButtonGroup
            v-if="props.showRowActions && !checkboxMultiselect && selectedRowsState.length"
            class="ml-2"
          >
            <Button
              outlined
              icon="pi pi-eye"
              :disabled="selectedRowsState.length !== 1"
              @click="viewSelected"
            />
            <Button
              outlined
              icon="pi pi-pencil"
              :disabled="selectedRowsState.length !== 1"
              @click="editSelected"
            />
          </ButtonGroup>
        </template>
        <template #center></template>
        <template #end>
          <Button
            v-if="props.showSecondaryToolbarButtons && props.showRefresh && props.onRefresh"
            v-tooltip="{
              value: t('titletags.toggleRefresh'),
              fitContent: true,
              showDelay: 300,
            }"
            icon="mdi mdi-refresh"
            class="p-button-text settings-button ml-2 -mr-4"
            aria-label="Refresh"
            @click="props.onRefresh"
          />
          <Button
            v-if="
              props.showSecondaryToolbarButtons &&
              props.reorderableRows &&
              !currentSortMeta.length &&
              !savedFilterString.length &&
              isPrefilterClear
            "
            v-tooltip="{
              value: t(
                reorderableRowsEnabled
                  ? 'titletags.reorderableRowsEnabled'
                  : 'titletags.reorderableRows',
              ),
              fitContent: true,
              showDelay: 300,
            }"
            :icon="reorderableRowsEnabled ? 'pi pi-sort-alt-slash' : 'pi pi-arrows-v'"
            class="p-button-text settings-button ml-2 -mr-4"
            aria-label="Reorder Rows"
            @click="reorderableRowsEnabled = !reorderableRowsEnabled"
          />
          <Button
            v-if="props.showSecondaryToolbarButtons && props.showSplitView"
            v-tooltip="{
              value: t(
                isSplitView ? 'titletags.toggleSplitviewOn' : 'titletags.toggleSplitviewOff',
              ),
              fitContent: true,
              showDelay: 300,
            }"
            :icon="isSplitView ? 'mdi mdi-fullscreen' : 'mdi mdi-arrow-split-vertical'"
            class="p-button-text settings-button ml-2 -mr-4"
            aria-label="Split View"
            @click="toggleSplitView"
          />
          <Button
            v-if="props.showSecondaryToolbarButtons"
            v-tooltip="{
              value: t('titletags.showTableSettings'),
              fitContent: true,
              showDelay: 300,
            }"
            icon="pi pi-sliders-h"
            class="p-button-text settings-button mx-2"
            aria-label="Settings"
            @click="settingsDrawerVisible = true"
          />

          <TableQuickFilter
            :data="prefilteredData"
            :columns="visibleColumns"
            :focusedColumns="focusedColumns"
            :filterString="savedFilterString"
            :enableAutoRestore="true"
            @update:filtered-data="(data) => updateQuickFilteredData(data)"
            @update:filter-string="handleFilterStringUpdate"
          />
        </template>
      </Toolbar>

      <DataTable
        v-if="isTableReady"
        :key="tableKey"
        v-model:selection="selectedRowsState"
        v-model:expandedRows="expandedRows"
        dataKey="__rowKey"
        :data="paginatedData"
        :value="paginatedData"
        :columns="visibleColumns"
        :isLoadingFinished="true"
        :rowClass="getRowclass"
        :multiSortMeta="currentSortMeta"
        :metaKeySelection="metaKey"
        :selectionMode="checkboxMultiselect ? undefined : selectionMode"
        :sortable="sortable"
        :removableSort="removableSort"
        :size="size"
        sortMode="multiple"
        :stripedRows="stripedRows"
        :showGridlines="cellGrid"
        :filter="filter"
        :reorderableColumns="!reorderableRowsEnabled && reorderableColumns"
        :class="[
          cellGrid ? 'cellgrid' : '',
          isSplitView ? 'split' : 'no-split',
          tableSurfaceClass,
          tableClass,
        ]"
        :rowGroupMode="effectiveRowGroupMode"
        :groupRowsBy="effectiveGroupRowsBy"
        :pt="dataTablePt"
        @rowContextmenu="onRowContextMenu"
        @sort="handleSort"
        @rowClick="handleRowClick"
        @rowDblclick="handleRowDblClick"
        @column-reorder="onColReorder"
        @row-reorder="onRowReorder"
        @update:selection="onSelectionUpdate"
      >
        <Column
          v-if="reorderableRowsEnabled && !currentSortMeta.length && !savedFilterString.length"
          :rowReorder="reorderableRows"
          headerStyle="width: 3rem"
          :reorderableColumn="false"
        />

        <!-- SelectingRow-Checkbox -->
        <Column
          v-if="checkboxMultiselect"
          :selectionMode="selectionMode"
          headerStyle="width: 3rem"
          :class="[`table-cell`]"
          :reorderableColumn="false"
        />

        <Column
          v-if="hasExpandedRow && collapsedExpandedRow"
          expander
          style="width: 3rem"
          :class="[`table-cell`]"
          :reorderableColumn="false"
        />
        <!-- Column content -->
        <Column
          v-for="(col, colIndex) in visibleColumns"
          :key="col?.field"
          :field="col?.field"
          :sortable="col?.sortable && sortable"
          :filter="col?.filter"
          :class="[
            `table-cell`,
            `text-${col?.align}`,
            col?.noWrap ? 'nowrap-cell' : '',
            getCellIdentifierClass(col),
          ]"
        >
          <template v-if="col" #header>
            <slot name="custom-header" :column="col" :columnIndex="colIndex">
              <div
                v-tooltip="{ value: col.title, fitContent: true, showDelay: 300 }"
                class="header-label"
                :data-testid="`datatable-header-${dataKey}-${col.field}`"
                :data-field="col.field"
              >
                <span>{{ getResponsiveHeaderLabel(col) }}</span>
              </div>
            </slot>
          </template>

          <template #body="slotProps">
            <slot
              :name="`body-${col.field}`"
              :row="slotProps.data"
              :column="col"
              :data-key="dataKey"
              :field="col.field"
            >
              <div
                class="sey-dt-cell-hitarea"
                :data-field="col.field"
                :data-row-id="String(getRowId(slotProps.data) ?? slotProps.index ?? 'unknown')"
                :data-testid="`datatable-cell-${dataKey}-${String(getRowId(slotProps.data) ?? slotProps.index ?? 'unknown')}-${col.field}`"
              >
                <DataTableCell
                  :cell="slotProps.data[col.field]"
                  :col="col"
                  :edit-mode="props.editMode"
                  :isSelected="isRowSelected(slotProps.data)"
                  :rowData="slotProps.data"
                  :isActive="isCellActive(slotProps.data, col.field)"
                  :bulkEditActive="bulkEditActive"
                  :bulkEditField="bulkEditField"
                  @cellEdit="(payload) => handleCellEditFromCell(slotProps.data, payload)"
                  @toggleExpansion="handleToggleExpansion"
                  @requestTab="handleTabFromCell"
                  @requestBlur="handleBlurFromCell"
                >
                  <template v-if="getEffectiveTemplate(col)" #display>
                    <component
                      :is="TemplateComponentCache[getEffectiveTemplate(col)!]"
                      :row="slotProps.data"
                      :column="col"
                      :cell="slotProps.data[col.field]"
                      :data-key="dataKey"
                    />
                  </template>
                </DataTableCell>
              </div>
            </slot>
          </template>

          <template v-if="hasFooterSlot(col.field)" #footer>
            <slot
              :name="`footer-${col.field}`"
              :column="col"
              :data-key="dataKey"
              :filtered-data="quickFilteredData"
            />
          </template>
        </Column>

        <!-- groupheader slot: only when rowGroupMode is active -->
        <template v-if="effectiveRowGroupMode === 'subheader'" #groupheader="slotProps">
          <slot
            name="group-header"
            :row="{ ...slotProps.data, other: {} }"
            :raw-row="unwrapRow(slotProps.data)"
            :checkboxMultiselect="checkboxMultiselect"
            :reorderableRows="reorderableRowsEnabled"
            :group-value="resolveGroupValue(slotProps.data, effectiveGroupRowsBy)"
          >
            <!-- default fallback if no group-header slot is provided -->
            {{ resolveGroupValue(slotProps.data, effectiveGroupRowsBy) }}
          </slot>
        </template>

        <!-- Expanded row -->
        <template v-if="hasExpandedRow" #expansion="slotProps: ExpandedTableRow">
          <slot
            name="custom-expanded-row"
            :row="slotProps.data"
            :visibleColumns="visibleColumns"
            :hasExpandedRow="hasExpandedRow"
            :collapsedExpandedRow="collapsedExpandedRow"
            :checkboxMultiselect="checkboxMultiselect"
            :reorderableRows="reorderableRowsEnabled"
          >
            Add your expanded content in a separate template
          </slot>
        </template>
      </DataTable>

      <Confirm ref="confirmDialog" />
      <ContextMenu ref="tableRowContextMenu" :model="contextMenuItems" />

      <Paginator
        v-if="
          footerPagination &&
          rowsPerPageOptions[0] &&
          (quickFilteredData.length > rowsPerPageOptions[0] || settingsDrawerVisible)
        "
        :first="currentPage * pageSize"
        :rows="pageSize"
        :pageLinkSize="3"
        :totalRecords="quickFilteredData.length"
        :rowsPerPageOptions="rowsPerPageOptions"
        class="mt-4"
        @page="handlePaginatorChange"
      />
    </div>
    <div v-else class="no-data">
      <p v-if="props.isLoadingFinished || props.isSubcomponent" class="my-4">
        {{ t('messages.noTableData') }}
      </p>
    </div>
    <TableSettingsDrawer
      v-model:visible="settingsDrawerVisible"
      :columns="columns"
      :visibleColumns="visibleColumns.map((col) => col?.field ?? '')"
      :data-key="dataKey"
      @update-settings="updateSettings"
      @export="handleExport"
      @update:visible-columns="handleVisibleColumnsChange"
      @update:size="handleSizeUpdate"
    />
  </div>
  <!-- for e2e tests -->
  <div
    :data-testid="`datatable-stats-${dataKey}`"
    :data-total="quickFilteredData.length"
    :data-visible="paginatedData.length"
    :data-pagesize="pageSize"
    :data-page="currentPage"
    style="display: none"
  />
</template>

<script setup lang="ts">
/**
 * DataTable.vue
 *
 * Highly configurable, stateful DataTable component built on top of PrimeVue.
 *
 * Responsibilities:
 * - Enrich raw data rows with meta information (search, render, sort, truncate).
 * - Manage selection (single, multi, range) independent of filtering/pagination.
 * - Support keyboard navigation and inline cell editing.
 * - Apply prefiltering, quick filtering, sorting and pagination in a deterministic pipeline.
 * - Persist table state (sorting, filters, visible columns, layout) via Pinia stores.
 *
 * Data flow (strict order):
 *   props.data
 *     → processedData        (meta enrichment)
 *       → prefilteredData    (prefilter tabs)
 *         → quickFilteredData (text search)
 *           → sortedData     (multi sort / reorder)
 *             → paginatedData (current page slice)
 *
 * Design principles:
 * - Meta enrichment happens exactly once per row.
 * - Selection is ID-based and survives filtering & pagination.
 * - UI state (selection, edit cell, expansion) is decoupled from data state.
 * - Complex behavior is delegated to composables.
 */

/**
 * External dependencies:
 * - PrimeVue DataTable & Column for rendering.
 * - Vue Router for row navigation.
 * - Pinia stores for persistent table state.
 *
 * Internal composables are intentionally small and focused:
 * - useDataTableMetaRows → meta enrichment & identity
 * - useDataTableSelection → selection state & intents
 * - useDataTableKeyboardNavigation → keyboard-driven focus & selection
 * - useDataTableCellEditActivation → click → edit activation logic
 * - useDataTableTechnicalColumns → checkbox / expander / reorder columns
 */

import {
  computed,
  effectScope,
  nextTick,
  onBeforeUnmount,
  onMounted,
  onUpdated,
  ref,
  toRaw,
  useSlots,
  watch,
} from 'vue';
import Column from 'primevue/column';
import DataTable, {
  type DataTableRowClickEvent,
  type DataTableRowDoubleClickEvent,
  type DataTableSortEvent,
  type DataTableSortMeta,
} from 'primevue/datatable';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import Confirm from '@/components/confirm/Confirm.vue';
import TableQuickFilter from '@/components/datatable/TableQuickFilter.vue';
import TableSettingsDrawer from '@/components/datatable/TableSettingsDrawer.vue';
import { useCsvExport } from '@/composables/useCsvExport';
import { useDataTableCellEditActivation } from '@/composables/useDataTableCellEditActivation';
import { useDataTableKeyboardNavigation } from '@/composables/useDataTableKeyboardNavigation';
import { useDataTableMetaRows } from '@/composables/useDataTableMetaRows';
import { useDataTableRowNavigation } from '@/composables/useDataTableRowNavigation';
import { useDataTableSelection } from '@/composables/useDataTableSelection';
import { useDataTableTechnicalColumns } from '@/composables/useDataTableTechnicalColumns';
import { useOutsideClickClose } from '@/composables/useOutsideClickClose';
import { useFallbackTranslation } from '@/composables/useTranslation';
import { useProfileStore } from '@/stores/profile.ts';
import { useSplitViewStore } from '@/stores/splitview';
import { useTableExpandedColumnsStore } from '@/stores/tableExpandedColumns';
import { useTableFilterStateStore } from '@/stores/tableFilter';
import { useTableLayoutSettingsStore } from '@/stores/tableLayoutSettings';
import { useTablePageSizeStore } from '@/stores/tablePagesize';
import { useTableRowSortStore } from '@/stores/tableRowSort';
import { useSizeStore } from '@/stores/tableSize';
import { type SortMeta, useTableSortStateStore } from '@/stores/tableSort';
import { useTableVisibleColumnsStore } from '@/stores/tableVisibleColumns';
import { useViewportStore } from '@/stores/viewport';
import type { PrefilterOption } from '@/utils/prefilter';
import { safeStringify } from '@/utils/string';
import { hasLogging } from '@/plugins/global-config';

import DataTableCell from './DataTableCell.vue';
import DataTablePrefilterTabs from './DataTablePrefilterTabs.vue';
import { TemplateComponentCache } from './dataTableTemplateCache';
import {
  type align,
  type ColumnConfig,
  type DataTableProps,
  type ExpandedTableRow,
  type MetaEnrichedRow,
  type RowRouteAction,
  type TabRequestPayload,
} from './dataTableTypes';

const { t } = useI18n();
const numberLocale = 'de-DE';
const sizeStore = useSizeStore();
const tableSortStateStore = useTableSortStateStore();
const tableFilterStateStore = useTableFilterStateStore();
const tablePageSizeStore = useTablePageSizeStore();
const visibleColumnsStore = useTableVisibleColumnsStore();
const expandedColumnsStore = useTableExpandedColumnsStore();
const tableLayoutSettingsStore = useTableLayoutSettingsStore();
const splitViewStore = useSplitViewStore();
const rowSortStore = useTableRowSortStore();
const viewportStore = useViewportStore();
const profileStore = useProfileStore();

// Routing
const route = useRoute();
const router = useRouter();
const tableKey = ref(0);
const expandedRows = ref<Record<string, boolean>>({});
const isSplitView = ref(false);

const syncSplitView = () => {
  isSplitView.value = !!(route.params.isSplitView ?? splitViewStore.isSplitView);
};
syncSplitView();

/**
 * Component props:
 * - `data`: raw input rows (never mutated)
 * - `columns`: optional column configuration
 * - `dataKey`: stable table identifier for persisted state
 * - `editMode`: enables inline editing & keyboard navigation
 *
 * Events:
 * - `update:selectedRows`: emitted whenever selection changes
 * - `prefilter-change`: emitted when prefilter tab changes
 */

const props = withDefaults(defineProps<DataTableProps>(), {
  selectionMode: 'multiple',
  applySort: undefined,
  removableSort: undefined,
  checkboxMultiselect: undefined,
  reorderableRows: true,
  tableActions: true,
  showSecondaryToolbarButtons: true,
  showSecondaryToolbar: true,
  showPrimaryToolbar: true,
  showRowActions: true,
  showRefresh: false,
  showDelete: true,
  showSplitView: true,
  hasExpandedRow: false,
  collapsedExpandedRow: true,
  sortable: true,
  filter: true,
  reorderableColumns: true,
  prefilterTabs: () => [],
  editMode: false,
  shortLabelsThreshold: 10,
});

/**
 * Determines the effective template name for a column.
 * Priority: explicit template > auto-detect from type
 * Returns undefined if no template should be used.
 */
function getEffectiveTemplate(col: ColumnConfig): keyof typeof TemplateComponentCache | undefined {
  // Priority 1: Explicit template in config
  if (col.template && TemplateComponentCache[col.template]) {
    return col.template;
  }

  // Priority 2: Auto-detect template from type for array types
  if (col.type && ['stringArray', 'dateArray', 'objectArray', 'enumArray'].includes(col.type)) {
    const templateName = col.type as keyof typeof TemplateComponentCache;
    if (TemplateComponentCache[templateName]) {
      return templateName;
    }
  }

  // Priority 3: Auto-detect dateCell for date type
  if (col.type === 'date' && TemplateComponentCache['dateCell']) {
    return 'dateCell';
  }

  return undefined;
}

const emit = defineEmits<{
  (e: 'update:selectedRows', value: MetaEnrichedRow<Record<string, any>>[]): void;
  (e: 'prefilter-change', payload: { option: PrefilterOption }): void;
}>();

// paginate filtered & sorted data
const paginatedData = ref<MetaEnrichedRow<Record<string, any>>[]>([]);

// Dynamic Columns related to configuration file
const visibleColumns = computed<ColumnConfig[]>(() => {
  const all = allColumns?.value ?? [];

  // If no rows known yet: simply return empty array
  if (!Array.isArray(all) || all.length === 0) {
    return [];
  }

  let columnKeysVisible: string[] = [];
  const storedVisibleColumns = visibleColumnsStore.getVisibleColumns(props.dataKey as string) ?? [];

  if (storedVisibleColumns.length) {
    columnKeysVisible = storedVisibleColumns.filter(isKeyVisible);
  } else if (props.columns?.length) {
    columnKeysVisible = props.columns
      .filter((column) => !column.hideInitial && !column.hidden)
      .map((column) => column.field);
  } else {
    columnKeysVisible = all.map((column) => column.field);
  }

  const filteredAndSortedColumns = all
    .filter((col) => columnKeysVisible.includes(col.field) || col.field === '_meta')
    .sort((a, b) => columnKeysVisible.indexOf(a.field) - columnKeysVisible.indexOf(b.field));

  return filteredAndSortedColumns;
});

const activeEditCell = ref<{ rowId: string | number; field: string } | null>(null);

// Define reactive variables
const tableRoot = ref<HTMLElement | null>(null);
const size = ref<any>(sizeStore.size || 'normal');
const metaKey = ref(true);
const currentSortMeta = ref<DataTableSortMeta[]>([]);
const prefilterSortMeta = ref<DataTableSortMeta[]>([]);
const pageSize = ref<number>(tablePageSizeStore.pageSize);
const currentPrefilterOption = ref<PrefilterOption | null>(null);
const settingsDrawerVisible = ref(false);
const stripedRows = ref(false);
const cellGrid = ref(false);
const checkboxMultiselect = ref(
  props.checkboxMultiselect !== undefined
    ? props.checkboxMultiselect
    : tableLayoutSettingsStore.checkboxMultiselect,
);
const footerPagination = ref(false);
const instantEdit = ref(false);
const confirmDialog = ref<InstanceType<typeof Confirm> | null>(null);
const currentPage = ref<number>(0); // Current page
const savedFilterString = ref<string>(''); // Saved filter string
const rowsPerPageOptions = [10, 20, 50];
const isTableReady = ref(false);
const editModeScope = ref<ReturnType<typeof effectScope> | null>(null);

const seg = (p: unknown): string =>
  Array.isArray(p) ? (safeStringify(p[0]) ?? '') : (safeStringify(p) ?? '');

// measure render time
const renderCount = ref(0); // re-render-count

/**
 * Data pipeline (immutable, unidirectional):
 *
 *  props.data
 *   → 0) rawData (unchanged)
 *     → 1) processedData (enriched mit Meta)
 *       → 2) prefilteredData (calculated once, updated onPrefilterChange)
 *         → 3) quickFilteredData (via quickFilter)
 *           → 4) sortedData (sorting applied)
 *             → 5) paginatedData (Slice of sortedData)
 *
 * 1) processedData
 *    - Enriched once via useDataTableMetaRows
 *    - Adds meta(searchString, rawvalue, renderedValue, etc.)
 *
 * 2) prefilteredData
 *    - Reduced via prefilter tabs
 *    - Does NOT mutate processedData
 *
 * 3) quickFilteredData
 *    - Text search across visible columns
 *    - Rebuilt whenever filter string or visible columns change
 *
 * 4) sortedData
 *    - Applies multi-sort or row order
 *
 * 5) paginatedData
 *    - Final slice rendered by PrimeVue DataTable
 *
 */

// process data from the props initially and enrich it with meta
const processedData = computed(() => {
  const tempData = props.data ?? [];
  return enrichRows(tempData); // <-- uses shared meta implementation
});

// narrow data due to prefiltering
const prefilteredData = computed(() => {
  const tempData = processedData.value ?? [];
  return currentPrefilterOption?.value
    ? applyPrefilterToProcessedData(tempData, currentPrefilterOption?.value)
    : tempData;
});

const isPrefilterClear = computed(() => {
  return prefilteredData.value.length === processedData.value.length;
});

// amount of extra columns for group headers
const extraGroupHeaderColumns = computed(() => {
  let extra = 0;

  // reordering column
  if (
    reorderableRowsEnabled.value &&
    !currentSortMeta.value.length &&
    !savedFilterString.value.length
  ) {
    extra += 1;
  }

  // checkbox selection column
  if (checkboxMultiselect.value) {
    extra += 1;
  }

  // expanded row column
  if (props.hasExpandedRow && props.collapsedExpandedRow) {
    extra += 1;
  }

  return extra;
});

// PassThrough configuration for dataTable fixing colspan issue
const dataTablePt = computed(() => ({
  // rowGroupHeaderCell = <td> of optional group header row
  rowGroupHeaderCell: (options: any) => {
    const baseColspan =
      (options?.attrs && options.attrs.colspan) ??
      // fallback: known visible columns
      visibleColumns.value.length;

    return {
      // override colspan: basic plus technical columns
      colspan: baseColspan + extraGroupHeaderColumns.value,
    };
  },

  table: {
    'data-testid': `datatable-table-${dataKey.value}`,
  },
  tbody: {
    'data-testid': `datatable-tbody-${dataKey.value}`,
  },

  bodyRow: (options: any) => {
    // PrimeVue PT can provide row/index either in `props` or in `context`
    const row =
      options?.props?.rowData ??
      options?.context?.rowData ??
      options?.context?.data ??
      options?.rowData ??
      options?.data ??
      undefined;

    const index =
      options?.props?.rowIndex ??
      options?.context?.index ??
      options?.context?.rowIndex ??
      options?.rowIndex ??
      undefined;

    const id = row ? getRowId(row) : undefined;
    const stableId = id ?? index ?? 'unknown';

    let routeId: string | undefined = undefined;
    if (typeof props.rowRouteId === 'function') {
      const v = props.rowRouteId(row);
      if (v != null) routeId = String(v);
    } else if (typeof props.rowRouteId === 'string') {
      // support meta-wrapped cell like number.meta('rawvalue')
      const key = props.rowRouteId;
      const cell = row[key];
      const raw = cell?.meta?.('rawvalue') ?? cell?.value ?? cell;
      if (raw != null) routeId = String(raw);
    }

    const dk = String(dataKey.value);

    return {
      'data-row-id': String(stableId),
      'data-row-index': String(index ?? ''),
      'data-testid': `datatable-row-${dk}-${String(stableId)}`,
      ...(routeId ? { 'data-route-id': routeId } : {}),
    };
  },

  // Remove padding from body cells when in edit mode (PrimeVue cell edit)
  column: {
    bodycell: ({ state }: any) => ({
      class: [{ '!py-0': state?.['d_editing'] }],
    }),
  },
}));

// quickfilter remaining data
const quickFilteredData = ref<MetaEnrichedRow<Record<string, any>>[]>([]);

// sorting the remaining data
const sortedData = computed(() => {
  const data = quickFilteredData.value ?? [];
  const sortFn = sortFunction(currentSortMeta.value);

  return [...data].sort(sortFn || (() => 0));
});

/**
 * Default row sort strategy:
 *
 * Order of precedence:
 * 1) Explicit multi-sort (PrimeVue)
 * 2) Position field (if present)
 * 3) Persisted row order (reorderable rows)
 *
 * Sorting operates on meta-comparable values
 * (rawvalue → searchString → primitive fallback).
 */

const defaultRowSort =
  (currentSortMeta: DataTableSortMeta[]) => (a: Record<string, any>, b: Record<string, any>) => {
    if (currentSortMeta.length === 0) {
      if (!!a.position) {
        // Sort by position if available
        const aPosition = getComparableValue(a as any, 'position') ?? 0;
        const bPosition = getComparableValue(b as any, 'position') ?? 0;
        return Number(aPosition) - Number(bPosition);
      } else if (props.reorderableRows) {
        // Sort by row index if reorderable
        return rowSortStore.getSortFunction(props.dataId || props.dataKey)(a, b);
      }
    }

    // Sort by current sort meta
    for (const { field, order } of currentSortMeta) {
      const fieldName = typeof field === 'string' ? field : undefined;
      if (!fieldName) continue;

      const aValue = getComparableValue(a as any, fieldName);
      const bValue = getComparableValue(b as any, fieldName);

      if (aValue == null && bValue == null) continue;
      if (aValue == null) return order === 1 ? -1 : 1;
      if (bValue == null) return order === 1 ? 1 : -1;

      // Normalize Date comparisons if needed
      const av = aValue instanceof Date ? aValue.getTime() : aValue;
      const bv = bValue instanceof Date ? bValue.getTime() : bValue;

      if (av < bv) return order === 1 ? -1 : 1;
      if (av > bv) return order === 1 ? 1 : -1;
    }

    return 0;
  };

const onPrefilterChange = (option: PrefilterOption) => {
  // Only update the data prefilter for "data" options.
  // Column-only options (e.g. negotiation column filter) should not reset the row filter.
  if (!option.affectsColumns) {
    currentPrefilterOption.value = option;
  }

  // Always inform the parent so it can react (e.g. update column store)
  emit('prefilter-change', { option });
};

const { selectionMode, sortable, filter } = props;

const removableSort = props.removableSort ?? true;

/**
 * Row interaction policy:
 *
 * Priority order:
 * 1) Ignore clicks on technical columns
 * 2) If editMode → activate cell editing
 * 3) If user handlers provided → delegate
 * 4) Fallback: navigate (view/edit depending on modifier keys)
 */

// Define handlers with proper typing
const handleRowClick = (event: DataTableRowClickEvent) => {
  const original = event.originalEvent as MouseEvent;

  // Do not run edit activation / selection logic when clicking technical columns
  if (isClickOnTechnicalColumn(original)) return;

  if (props.editMode) {
    activateFromRowClickEvent(event);
    return;
  }

  if (props.onRowClick) {
    props.onRowClick(event);
    return;
  }

  defaultRowClick(event);
};

const handleRowDblClick = (event: DataTableRowDoubleClickEvent) => {
  const original = event.originalEvent as MouseEvent;

  if (isClickOnTechnicalColumn(original)) {
    return;
  }

  if (props.editMode) {
    return;
  }

  if (props.onRowClick) {
    props.onRowClick(event);
    return;
  }
  defaultRowClick(event);
};

const sortFunction = props.applySort || defaultRowSort;
const reorderableRowsEnabled = ref(false);

const focusedColumns = computed<string[]>(
  () =>
    currentSortMeta.value
      .map((meta) => meta.field)
      .filter((field): field is string => typeof field === 'string'), // Type Guard
);

const isKeyVisible = (columnKey: string) => {
  const isVisible = !props.columns?.find((a) => a.field === columnKey)?.hidden;
  return isVisible;
};

// Automatically generate columns if none are provided
const allColumns = computed<ColumnConfig[]>(() => {
  const data = props.data ?? [];
  const columns = props.columns ?? [];
  // Check if props.data is available and has elements
  if (data.length === 0) {
    return columns;
  }

  if (hasColumns(props)) {
    return configuredDataTableProperties(props.columns);
  }

  return calculatedDataTableProperties();
});

const dataKey = ref(props.dataKey || allColumns.value.map((c) => c.field).join('-'));

const { enrichRows, unwrapRow, getRowId, getComparableValue } = useDataTableMetaRows({
  allColumns,
  dataKey: computed(() => dataKey.value),
  expandedColumnsStore,
  t,
  locale: computed(() => numberLocale),
});

/**
 * Selection system:
 *
 * - Selection is ID-based (`getRowId`) and independent of data slices.
 * - Supports:
 *   - single replace
 *   - additive (Ctrl)
 *   - range (Shift)
 * - Selection is reapplied after pagination & filtering.
 *
 * Source of truth:
 * - selectedRowIds (IDs)
 * - selectedRowsState (row objects for PrimeVue binding)
 */

const {
  selectedRowsState,
  selectedRowIds,
  selectionAnchorRowId,
  isRowSelected,
  reapplySelectionFromIds,
  replaceSelectionWithRow,
  addRowToSelection,
  ensureRowSelected,
  selectRangeToRow,
  applySelectionIntent,
  onSelectionUpdate,
} = useDataTableSelection({
  paginatedData,
  getRowId,
  emitSelectedRows: (rows) => emit('update:selectedRows', rows),
});

const { navigateToRow, navigateSelectedRow, viewSelected, editSelected } =
  useDataTableRowNavigation({
    route,
    router,
    dataKey: computed(() => dataKey.value),
    isSubcomponent: props.isSubcomponent,
    getRowId,
    selectedRowsState,
  });

/**
 * Keyboard navigation & edit activation:
 *
 * - Keyboard navigation operates only on editable cells.
 * - Vertical navigation also applies selection intent.
 * - Horizontal navigation moves within the editable grid.
 *
 * Edit activation:
 * - Clicking a body cell activates edit mode (if editable).
 * - Technical columns (checkbox, expander, reorder) are ignored.
 */

const { handleTabRequest } = useDataTableKeyboardNavigation({
  paginatedData,
  visibleColumns,
  getRowId,
  activeEditCell,
  ensureRowSelected,
  replaceSelectionWithRow,
  addRowToSelection,
  selectRangeToRow,
});

const areEqual = (a: any, b: any): boolean => {
  // if both values contain metadata, compare searchstring
  const aSearch = a?.meta?.('searchString');
  const bSearch = b?.meta?.('searchString');

  if (aSearch !== undefined && bSearch !== undefined) {
    return aSearch === bSearch;
  }

  // fallback: compare primitive values directly
  return a === b;
};

const getRowclass = (data: any) => {
  let clusterClassStart = '';
  let clusterClassEnd = '';
  let rowtypeClass = '';

  // paginatedData

  if (currentSortMeta.value?.length === 1) {
    const sortKey =
      typeof currentSortMeta.value[0]?.field === 'string'
        ? currentSortMeta.value[0]?.field
        : undefined;

    const col = props.columns.find((col) => col.field === sortKey);

    if (col?.type === 'enum' || col?.type === 'boolean' || !!col?.clusterable) {
      let info = undefined;
      if (sortKey) {
        info = data[sortKey]?.meta;
      }

      if (!!info) {
        const rawData = toRaw(data); // prevents recursive vue-dependencies
        const currentValue = sortKey ? rawData[sortKey]?.meta?.('searchString') : undefined;
        const currentID = getRowId(rawData);
        const IDX = paginatedData.value.findIndex(
          (row) => String(getRowId(row)) === String(currentID),
        );

        const prevValue = sortKey
          ? paginatedData.value[IDX - 1]?.[sortKey]?.meta?.('searchString')
          : undefined;
        const nextValue = sortKey
          ? paginatedData.value[IDX + 1]?.[sortKey]?.meta?.('searchString')
          : undefined;

        rowtypeClass = `type-${col?.type}`;
        clusterClassStart =
          areEqual(prevValue, currentValue) || prevValue === undefined
            ? 'connected-row-start'
            : 'elevated-row-start';
        clusterClassEnd =
          areEqual(nextValue, currentValue) || nextValue === undefined
            ? props.hasExpandedRow
              ? 'connected-row-end-before-expanded'
              : 'connected-row-end'
            : props.hasExpandedRow
              ? 'elevated-row-end-before-expanded'
              : 'elevated-row-end';
      }
    }
  }

  const rowClass = props.rowClass ? props.rowClass(data) : '';

  const isSelected = isRowSelected(data);
  const selectedClass = isSelected ? 'sey-dt-row--selected' : '';

  return [rowClass, selectedClass, clusterClassStart, clusterClassEnd, rowtypeClass].join(' ');
};

const bulkEditColumnClass = computed(() => {
  // Bulk only: more than 1 selected row AND we have an active edit cell
  if (selectedRowIds.value.length <= 1 || !activeEditCell.value?.field) return '';

  // Normalize field so it matches your CSS-friendly naming
  const field = normalizeForClass(activeEditCell.value.field);
  if (!field) return '';

  return `sey-dt-bulkcol--${field}`;
});

function normalizeForClass(value: unknown): string {
  if (!value) return '';
  // Handle objects safely - only use primitive values
  if (typeof value === 'object') {
    return '';
  }
  // At this point, value is a primitive (string, number, boolean, etc.)
  const strValue =
    typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
      ? String(value)
      : '';
  return strValue.toLowerCase().replace(/[^a-z0-9\-]+/g, '-'); // replace everything non-alphanumeric with "-"
}

function getCellIdentifierClass(col?: ColumnConfig): string {
  if (!col?.field) return '';
  // sey-dt-editable-cell-active-td`
  const dk = normalizeForClass(dataKey.value);
  const field = normalizeForClass(col.field);
  if (!dk || !field) return '';
  return `sey-dt-cell--${dk}--${field} ${col.editable && props.editMode ? 'sey-dt-editable-cell' : ''}`;
}

const { getBodyCellIndexFromEvent, isClickOnTechnicalColumn, toVisibleColumnIndex } =
  useDataTableTechnicalColumns({
    checkboxMultiselect,
    hasExpandedRow: computed(() => props.hasExpandedRow),
    collapsedExpandedRow: computed(() => props.collapsedExpandedRow),
    reorderableRowsEnabled,
    currentSortMeta,
    savedFilterString,
  });

const { activateFromRowClickEvent } = useDataTableCellEditActivation({
  visibleColumns,
  getRowId,
  activeEditCell,
  selectionAnchorRowId,
  applySelectionIntent,
  getBodyCellIndexFromEvent,
  toVisibleColumnIndex,
});

const addLabel = useFallbackTranslation(`labels.${props.dataKey}.add`, 'labels.add');

function getResponsiveHeaderLabel(col: ColumnConfig): string {
  const baseKey = (col as any).baseHeaderKey ?? col.header ?? col.field;
  const fullKey = `labels.${baseKey}`;
  const shortKey = `labels.${baseKey}Short`;

  const fullLabel = t(fullKey);
  const shortLabel = t(shortKey);
  const hasShortLabel = shortLabel !== shortKey;

  // 2) Compact heuristic: reacts to viewport + visible columns
  const visibleCount = visibleColumns.value.length;
  const isCompact = viewportStore.isNarrow || visibleCount > props.shortLabelsThreshold;

  if (isCompact && hasShortLabel) {
    return shortLabel;
  }

  return fullLabel;
}

function isCellActive(row: any, field: string): boolean {
  const rowId = getRowId(row);
  if (!rowId || !activeEditCell.value) return false;
  return (
    String(activeEditCell.value.rowId) === String(rowId) && activeEditCell.value.field === field
  );
}

const getAlignment = (col: ColumnConfig) => {
  return (
    col.align ||
    (props.data[0] &&
    (typeof props.data[0][col.field] === 'boolean' || typeof props.data[0][col.field] === 'number')
      ? 'right'
      : 'left')
  );
};

const calculatedDataTableProperties = () => {
  const tableProperties = Object.keys(props.data?.[0] || []).map((key) => {
    const value = props.data?.[0]?.[key];
    const header = t(`labels.${key}`, key);
    const align: align = typeof value === 'boolean' || typeof value === 'number' ? 'right' : 'left'; // Default alignment

    return {
      field: key,
      header,
      sortable: true,
      filter: true,
      renderCell: undefined,
      align,
    };
  });

  hasLogging('sey-datatable') &&
    console.log(
      '[data-table] calculated keys:',
      tableProperties.map((p) => p.field),
    );

  return tableProperties;
};

const configuredDataTableProperties = (columns: ColumnConfig[]) => {
  const tableProperties = columns.map((col) => {
    const baseKey = col.header ?? col.field;
    const align: align = getAlignment(col);

    return {
      ...col,
      baseHeaderKey: baseKey,
      title: t(`labels.${baseKey}`),
      header: t(`labels.${baseKey}`), // "static" fallback
      sortable: col.sortable !== false,
      filter: col.filter !== false,
      renderCell: col.renderCell,
      align,
    };
  });

  return tableProperties;
};

function handleBlurFromCell() {
  activeEditCell.value = null;
}

// Handle cell edit from DataTableCell component
function handleCellEditFromCell(
  rowData: any,
  payload: { field: string; oldValue: any; newValue: any },
) {
  const rowId = getRowId(rowData);
  if (!rowId || !props.onCellEdit) return;

  const field = payload.field;
  const newValue = payload.newValue;

  const isBulk = selectedRowIds.value.length > 1;

  if (isBulk) {
    // Build a single batch payload
    const edits = selectedRowIds.value
      .map((id) => {
        // Prefer: find the selected row object you have in state
        const selectedRow =
          selectedRowsState.value.find((r) => String(getRowId(r)) === String(id)) ?? null;

        if (!selectedRow) return null;

        const cell = selectedRow[field];
        const oldValue = cell?.meta?.('rawvalue');

        return {
          rowId: String(id),
          oldValue,
          row: unwrapRow(selectedRow),
        };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);

    props.onCellEdit({
      mode: 'bulk',
      field,
      newValue,
      edits,
    });

    return;
  }

  // Single row payload (unchanged semantics)
  props.onCellEdit({
    mode: 'single',
    rowId: String(rowId),
    field,
    newValue,
    oldValue: payload.oldValue,
    row: unwrapRow(rowData),
  });
}

// Resolve the "visible" group value (based on cfg.groupBy) from raw DTO
const resolveGroupValue = (row: any, groupByPath?: string) => {
  if (!groupByPath) return null;

  const raw = unwrapRow(row);
  return groupByPath.split('.').reduce((acc: any, key: string) => {
    return acc ? acc[key] : undefined;
  }, raw);
};

// Internal technical field name used for grouping (primitive key)
const groupKeyField = computed<string | undefined>(() => {
  const cfg = props.rowGroupConfig;
  if (!cfg || !cfg.groupBy) return undefined;

  // e.g. groupBy = 'invoiceId' -> _group_invoiceId
  //      groupBy = 'invoice.id' -> _group_invoice_id
  const base = cfg.groupBy.replace(/\./g, '_');
  return `_group_${base}`;
});

// Compute if row grouping is currently active, based on config + current sort
const isRowGroupingActive = computed(() => {
  const cfg = props.rowGroupConfig;
  if (!cfg) return false;

  if (!cfg.activateOnSortFields || cfg.activateOnSortFields.length === 0) {
    return true;
  }

  const sortedFields = currentSortMeta.value
    .map((m) => (typeof m.field === 'string' ? m.field : undefined))
    .filter((f): f is string => !!f);

  if (!sortedFields.length) return false;

  const firstField = sortedFields[0];
  return firstField ? cfg.activateOnSortFields.includes(firstField) : false;
});

// Effective rowGroupMode and groupRowsBy passed to PrimeVue
const effectiveRowGroupMode = computed<'subheader' | undefined>(() => {
  const cfg = props.rowGroupConfig;
  if (!cfg || !isRowGroupingActive.value) return undefined;
  return cfg.mode ?? 'subheader';
});

const effectiveGroupRowsBy = computed<string | undefined>(() => {
  const cfg = props.rowGroupConfig;
  if (!cfg || !isRowGroupingActive.value) return undefined;
  return groupKeyField.value;
});

const tableSurfaceClass = computed(() => {
  const ctx = props.tableSurfaceContext ?? 'ground';
  switch (ctx) {
    case 'card':
      return 'sey-datatable--surface-card';
    case 'section':
      return 'sey-datatable--surface-section';
    case 'ground':
    default:
      return 'sey-datatable--surface-ground';
  }
});

const slots = useSlots();
const hasFooterSlot = (field: string) => {
  // Check if parent provided a slot like "footer-myField"
  return !!slots[`footer-${field}`];
};

function hasColumns(props: DataTableProps) {
  return Array.isArray(props.columns) && props.columns.length > 0;
}

// Updates columns visibility in store
function handleVisibleColumnsChange(newVisibleColumns: string[]) {
  visibleColumnsStore.setVisibleColumns(props.dataKey as string, newVisibleColumns);
}

function handleSizeUpdate(newSize: string) {
  size.value = newSize;
}

// checks if cell is a functional cell at start of table (like checkbox or rowtoggle)
const cellIsClickable = (event: any) => {
  const cell = (event.originalEvent.target as HTMLElement).closest('td');

  if (!cell) return;
  const notClickableIndex =
    0 +
    Number(!!checkboxMultiselect.value) +
    Number(!!props.hasExpandedRow && !!props.collapsedExpandedRow);
  const cellIndex = Array.from(cell.parentElement?.children || []).indexOf(cell);

  return cellIndex >= notClickableIndex;
};

function defaultRowClick(event: any) {
  if (!cellIsClickable(event)) return;
  if (props.onRowDblClick) {
    props.onRowDblClick(event);
  } else {
    defaultRowDblClick(event);
  }
}

function defaultRowDblClick(event: any) {
  if (!cellIsClickable(event)) return;
  const action: RowRouteAction = event.originalEvent.altKey || instantEdit.value ? 'edit' : 'view';
  navigateToRow(event.data, action);
}

/**
 * Pagination:
 *
 * - `updatePaginatedData` is the ONLY place mutating paginatedData.
 * - Reapplies selection after slicing.
 * - Injects grouping keys when row grouping is active.
 */

function updatePaginatedData() {
  const start = currentPage.value * pageSize.value;
  const end = start + pageSize.value;
  paginatedData.value = sortedData.value.slice(start, end);

  // if row grouping is configured, add primitive group key for PrimeVue
  if (props.rowGroupConfig && groupKeyField.value) {
    const pathParts = props.rowGroupConfig.groupBy.split('.');

    paginatedData.value.forEach((row: any) => {
      const raw = unwrapRow(row);

      let value: any = raw;
      for (const part of pathParts) {
        value = value ? value[part] : undefined;
      }

      // prime, primitive grouping value
      row[groupKeyField.value as string] = value;
    });
  }

  if (!props.collapsedExpandedRow) {
    const map: Record<string, boolean> = {};
    for (const row of paginatedData.value) {
      // const id = row?.id?.meta?.('rawvalue') ?? row?.id;
      const id = getRowId(row);

      const metaValue = row?._meta?.meta?.('rawvalue') ?? row?._meta?.value ?? row?._meta;
      const allow = !metaValue || metaValue.hasExpandableData;
      if (id != null && allow) map[String(id)] = true;
    }
    expandedRows.value = map;
  }

  if (paginatedData.value?.length) {
    isTableReady.value = true;
  }

  // reapply selection for rebuilt rows
  reapplySelectionFromIds();
}

function updatePageSize(newPageSize: number) {
  if (newPageSize === pageSize.value) return;
  pageSize.value = newPageSize;
  tablePageSizeStore.setPageSize(newPageSize);
  currentPage.value = 0; // Back to first page, if pagesize changed
}

function handlePaginatorChange(event: { page: number; rows: number }) {
  currentPage.value = event.page;
  updatePageSize(event.rows); // setting pageSize + currentPage
}

function handleToggleExpansion(colField: string) {
  const isCurrentlyExpanded = expandedColumnsStore.isColumnExpanded(dataKey.value, colField);
  expandedColumnsStore.setExpandedColumn(dataKey.value, colField, !isCurrentlyExpanded);

  refreshTable();
}

function selectRowByID() {
  const rowIDsselected: string[] = [];

  const urlID = Array.isArray(route.params.id) ? route.params.id[0] : String(route.params.id ?? '');

  if (props.selectedRows && props.selectedRows.length > 0) {
    props.selectedRows.forEach((row) => {
      const id = getRowId(row);
      if (id != null) rowIDsselected.push(String(id));
    });
  } else if (urlID && paginatedData.value.length) {
    rowIDsselected.push(String(urlID));
  }

  const rows = paginatedData.value.filter((item) => {
    const id = getRowId(item);
    return id != null && rowIDsselected.includes(String(id));
  });

  if (rows.length) {
    selectedRowsState.value = rows;
    // keep id-state in sync
    selectedRowIds.value = rows.map((r) => String(getRowId(r)!));
  }
}

function refreshTable() {
  tableKey.value += 1; // enforces re-render of table
}

const handleFilterStringUpdate = (filterString: string) => {
  if (props.dataKey) {
    savedFilterString.value = filterString;
    tableFilterStateStore.setFilterString(props.dataKey, filterString);
  }
};

function handleCreateClick() {
  if (props.onCreate) {
    props.onCreate();
  } else {
    const comp = seg(route.params.component);
    const pid = seg(route.params.id);
    if (props.isSubcomponent) {
      void router.push(`/${comp}/${pid}/${props.dataKey}/new`);
    } else {
      void router.push(`/${comp}/new`);
    }
  }
}

function updateQuickFilteredData(data: any[]) {
  quickFilteredData.value = data;
}

function loadInitialSortOrder() {
  if (dataKey.value) {
    const initialSortMeta = tableSortStateStore.getSortOrder(dataKey.value);

    if (initialSortMeta && initialSortMeta.length > 0) {
      currentSortMeta.value = initialSortMeta.map((meta) => ({
        field: meta.field,
        order: meta.order as 1 | -1,
      }));
    }
  }
}

const getIdsToDelete = () => {
  return selectedRowsState.value
    .map((item) => getRowId(item))
    .filter((id): id is string | number => id != null);
};

function deleteSelected() {
  if (!props.onDelete) return;

  const ids = getIdsToDelete();

  props.onDelete(ids);

  selectedRowsState.value = [];
  selectedRowIds.value = [];
}

function onRowContextMenu(event: any) {
  const e = event.originalEvent as MouseEvent | undefined;
  const row = event.data;

  if (!e || !row) return;

  e.preventDefault();

  // Only replace selection if the row is not already selected
  if (!isRowSelected(row)) {
    applySelectionIntent(row, 'replace');
  }

  tableRowContextMenu.value?.show(e);
}

const tableRowContextMenu = ref();
const contextMenuItems = computed(() => {
  const selectedRows = selectedRowsState.value;
  const items: any[] = [];

  if (!props.tableActions) {
    items.push({
      label: t('titletags.showTableSettings'),
      icon: 'pi pi-sliders-h',
      command: () => {
        settingsDrawerVisible.value = true;
      },
    });
    return items;
  }

  if (selectedRows?.length === 1) {
    items.push({
      label: t('labels.view'),
      icon: 'pi pi-eye',
      command: () => navigateSelectedRow('view'),
    });
    items.push({
      label: t('labels.edit'),
      icon: 'pi pi-pencil',
      command: () => navigateSelectedRow('edit'),
    });
  }

  items.push({
    label: t('labels.delete'),
    icon: 'pi pi-trash',
    command: () => confirmDeleteSelected(),
  });

  return items;
});

const toggleSplitView = () => {
  const newStatus = !isSplitView.value;
  const pathChunks = route.path.split('/').filter(Boolean);
  const isDetailView = pathChunks.length >= 2; // e.g. /contacts/someuid
  splitViewStore.toggleViewMode();
  isSplitView.value = newStatus;
  const isNowSplitView = splitViewStore.isSplitView;

  if (isDetailView && !isNowSplitView) {
    window.location.reload(); // reload to show full detail view
    return;
  }
};

function handleSave() {
  if (!props.onSave) return;
  void props.onSave();
}

async function confirmDeleteSelected() {
  if (!confirmDialog.value) return;

  const isConfirmed = await confirmDialog.value.isDeleteConfirmed(
    props.confirmationMessage ? props.confirmationMessage(getIdsToDelete()) : undefined,
  );

  if (isConfirmed) {
    deleteSelected();
  }
}

function handleSort(event: DataTableSortEvent) {
  if (!event.multiSortMeta) return;

  currentSortMeta.value = event.multiSortMeta;
  tableSortStateStore.setSortOrder(dataKey.value, event.multiSortMeta as SortMeta[]);

  // Do NOT sort quickFilteredData here.
  // sortedData will react automatically, and the watcher(sortedData) updates pagination.
}

function reorderVisibleColumnFields(dragIndex: number, dropIndex: number) {
  const current = (
    visibleColumnsStore.getVisibleColumns(props.dataKey as string) ??
    visibleColumns.value.map((c) => c.field)
  ).slice();

  const [moved] = current.splice(dragIndex, 1);
  if (!moved) return;
  current.splice(dropIndex, 0, moved);

  visibleColumnsStore.setVisibleColumns(props.dataKey as string, current);
}

const onColReorder = (e: any) => {
  const { dragIndex: dragIndexRaw, dropIndex: dropIndexRaw } = e;

  let threshold = 0;
  if (!!checkboxMultiselect.value) threshold++;
  if (!!props.hasExpandedRow) threshold++;

  const dragIndex = dragIndexRaw - threshold;
  const dropIndex = dropIndexRaw - threshold;

  if (dragIndex < 0 || dropIndex < 0) return;

  reorderVisibleColumnFields(dragIndex, dropIndex);
};

const onRowReorder = (e: any) => {
  const { dragIndex, dropIndex } = e; // indexes in this page
  const pageStartIndex = currentPage.value * pageSize.value;

  const absoluteDragIndex = pageStartIndex + dragIndex; // index in full dataset
  const absoluteDropIndex = pageStartIndex + dropIndex; // index in full dataset

  if (
    dragIndex < 0 ||
    dropIndex < 0 ||
    dragIndex >= paginatedData.value.length ||
    dropIndex >= paginatedData.value.length
  ) {
    console.warn('Invalid indexes for reordering:', { dragIndex, dropIndex });
    return;
  }

  const movedItemInPaginatedData = paginatedData.value.splice(dragIndex, 1)[0];
  const movedItemInFilteredData = quickFilteredData.value.splice(absoluteDragIndex, 1)[0];

  if (movedItemInPaginatedData) {
    paginatedData.value.splice(dropIndex, 0, movedItemInPaginatedData);
  }
  if (movedItemInFilteredData) {
    quickFilteredData.value.splice(absoluteDropIndex, 0, movedItemInFilteredData);
  }

  props.dataKey &&
    rowSortStore.setRowOrder(
      props.dataId || props.dataKey,
      quickFilteredData.value
        .map((item) => getRowId(item))
        .filter((id): id is string | number => id != null)
        .map((id) => String(id)),
    );
};

const bulkEditActive = computed(() => {
  // Bulk preview only in edit mode and if multiple rows are selected
  return props.editMode && selectedRowIds.value.length > 1;
});

const bulkEditField = computed(() => {
  // Field being edited (the one that will be applied to all selected rows)
  // Only expose it when bulk is active to avoid confusing highlights
  if (!bulkEditActive.value) return null;
  return activeEditCell.value?.field ?? null;
});

const updateSettings = ({ key, value }: { key: string; value: boolean }) => {
  switch (key) {
    case 'stripedRows':
      stripedRows.value = value;
      break;
    case 'cellGrid':
      cellGrid.value = value;
      break;
    case 'checkboxMultiselect':
      checkboxMultiselect.value =
        props.checkboxMultiselect !== undefined ? props.checkboxMultiselect : value;
      break;
    case 'footerPagination':
      footerPagination.value = value;
      break;
    case 'instantEdit':
      instantEdit.value = value;
      break;
    default:
      hasLogging('sey-datatable') && console.warn(`Unknown key: ${key}`);
  }
  hasLogging('sey-datatable') && console.log(`${key} updated to ${value}`);
};

function applyPrefilterToProcessedData(
  procdata: MetaEnrichedRow<Record<string, any>>[],
  option: PrefilterOption,
) {
  let prefilteredTempData;

  if (option.customFilterFn) {
    prefilteredTempData = option.customFilterFn(procdata, focusedColumns.value);
  } else if (option.filterString) {
    const [key, rawVal] = option.filterString.split(':');
    const value =
      rawVal === 'true' ? true : rawVal === 'false' ? false : (rawVal?.toLowerCase?.() ?? rawVal);

    prefilteredTempData = procdata.filter((item) => {
      if (!key) return false;
      const metaValue = item[key];
      const rawValue = metaValue?.meta?.('rawvalue');
      return `${rawValue}` === `${value}`;
    });
  } else {
    prefilteredTempData = [...processedData.value];
  }

  if (option.sortMeta) {
    prefilterSortMeta.value =
      option.sortMeta?.map((meta) => ({
        field: meta.field,
        order: meta.order as 1 | -1,
      })) ?? [];
  }

  return prefilteredTempData;
}

const { exportCSV } = useCsvExport({
  dataKey: computed(() => props.dataKey ?? ''),
  processedData: prefilteredData,
  allColumns: computed(() => allColumns.value.map((col) => col.field)),
  visibleColumns: computed(() => visibleColumns.value.map((col) => col.field)),
  sortedData: sortedData,
});

const handleExport = (payload: { dataType: string; exportScope: string; columnScope: string }) => {
  exportCSV({
    toBeExported: payload.exportScope == 'all' ? processedData.value : sortedData.value,
    useRawData: payload.dataType === 'raw',
    showAllColumns: payload.columnScope === 'all',
  });
};

function handleTabFromCell(payload: TabRequestPayload) {
  handleTabRequest(payload);
}

/**
 * Reactive watchers:
 *
 * - Rebuild pagination when:
 *   - sorting
 *   - page
 *   - page size
 *
 * - Rebuild quick filter base when:
 *   - prefilter
 *   - search string
 *   - visible columns
 *
 * - Edit mode watcher uses effectScope to safely register/unregister listeners.
 */

watch(
  () => paginatedData.value,
  (newData) => {
    // initial select or when currently NOTHING is selected
    if (newData && newData.length > 0 && selectedRowIds.value.length === 0) {
      // only apply initial selection from URL/props,
      // never override user multi-selection
      selectRowByID();
    }
  },
  { immediate: true },
);

watch(route, () => {
  syncSplitView();
});

watch(
  () => props.selectedRows,
  (v) => {
    if (Array.isArray(v)) selectedRowsState.value = v;
  },
  { immediate: true },
);

watch([sortedData, currentPage, pageSize], () => updatePaginatedData(), { immediate: true });

watch(
  () => props.editMode,
  (isEdit) => {
    // Stop previous scope
    editModeScope.value?.stop();
    editModeScope.value = null;

    if (!isEdit) {
      activeEditCell.value = null;
      return;
    }

    // Start new scoped effects/listeners
    const scope = effectScope();
    scope.run(() => {
      useOutsideClickClose({
        enabled: computed(() => true), // always true inside this scope
        rootEl: tableRoot,
        onOutside: () => {
          activeEditCell.value = null;
        },
      });
    });

    editModeScope.value = scope;
  },
  { immediate: true },
);

/**
 * Lifecycle:
 *
 * onMounted:
 * - Restore persisted filter & sort state
 * - Initialize table layout settings
 * - Perform initial selection
 *
 * onUpdated:
 * - Debug render count (optional)
 *
 * onBeforeUnmount:
 * - Clean up editMode effect scope
 */

function initLayoutSettingsFromStore() {
  stripedRows.value = tableLayoutSettingsStore.stripedRows;
  cellGrid.value = tableLayoutSettingsStore.cellGrid;
  checkboxMultiselect.value =
    props.checkboxMultiselect !== undefined
      ? props.checkboxMultiselect
      : tableLayoutSettingsStore.checkboxMultiselect;
  footerPagination.value = tableLayoutSettingsStore.footerPagination;
  instantEdit.value = tableLayoutSettingsStore.instantEdit;
}

async function bootstrapTable() {
  // warnings
  if (!props.columns || props.columns.length === 0) {
    hasLogging('sey-datatable') &&
      console.warn('[data-table] No columns configuration provided. columns will be calculated');
  }
  if (!props.dataKey) {
    hasLogging('sey-datatable') &&
      console.warn('[data-table] No data key provided. Table state might not be stored properly');
  }

  // restore filter
  savedFilterString.value = tableFilterStateStore.getFilterString(props.dataKey ?? dataKey.value);

  // restore sort (after nextTick so computed columns/dataKey are stable)
  await nextTick();
  loadInitialSortOrder();

  // initial pagination + selection
  updatePaginatedData();
  selectRowByID();

  // layout settings
  initLayoutSettingsFromStore();
}

onMounted(async () => {
  await bootstrapTable();
});

// count render cycles
onUpdated(() => {
  renderCount.value++;
});

onBeforeUnmount(() => {
  editModeScope.value?.stop();
  editModeScope.value = null;
});
</script>

<style lang="css">
@reference "tailwindcss";
@reference "tailwindcss/utilities";

@layer app {
  .p-datatable-header-cell.text-center {
    padding-left: 2.25rem;
  }

  .p-datatable-column-header-content {
    @apply font-bold;
    justify-content: flex-end;
  }

  .p-datatable-column-header-content * {
    order: 1;
    /* Standard-Reihenfolge */
  }

  .p-datatable-column-header-content .p-badge {
    order: 2;
  }

  .p-datatable-column-header-content [data-pc-section='sort'] {
    order: 3;
    margin-left: 0;
  }

  .text-right .p-datatable-column-header-content * {
    order: 3;
    /* Standard-Reihenfolge */
  }

  .text-right .p-datatable-column-header-content .p-badge {
    order: 2;
  }

  .text-right .p-datatable-column-header-content [data-pc-section='sort'] {
    order: 1;
    margin-right: 0;
  }

  .p-datatable-table .p-datatable-column-header-content .header-label {
    flex-grow: 1;
    align-self: flex-start;
  }

  .p-datatable-gridlines .table-cell {
    border-width: 1px;
  }

  .p-datatable-gridlines .table-cell {
    border-width: 1px;
  }

  .p-datatable-sortable-column .p-datatable-sort-icon {
    opacity: 0;
  }

  .p-datatable-sortable-column.p-datatable-column-sorted .p-datatable-sort-icon {
    opacity: 1;
  }

  .p-datatable-sortable-column:hover .p-datatable-sort-icon {
    opacity: 1;
  }

  .p-toolbar .p-paginator {
    @apply p-0;
    /* just paginator inside toolbar */
  }

  .p-paginator-content {
    @apply flex items-center justify-center w-full;
    flex-wrap: nowrap;
  }

  .singlepage .p-disabled,
  .singlepage .p-paginator-pages {
    display: none;
  }

  .singlepage .p-paginator {
    @apply px-0;
  }

  .p-datatable .p-row-toggler {
    text-align: center;
  }

  .p-datatable .p-row-expansion {
    background-color: #f9f9f9;
  }

  .elevated-row td {
    border-top: 0.6em solid var(--surface-ground) !important;
  }

  .elevated-row-start td {
    border-top: 0.6em solid var(--surface-ground) !important;
  }

  .p-datatable-row-group-header ~ .p-datatable-row-group-header td {
    border-top: 0.6em solid var(--surface-ground) !important;
  }

  .p-datatable-row-group-header + .elevated-row-start td,
  .sey-datatable--surface-card .p-datatable-row-group-header + .elevated-row-start td {
    border-top: none !important;
  }

  .elevated-row-end td {
    border-bottom: 0.6em solid var(--surface-ground) !important;
  }

  .elevated-row-end-before-expanded + tr > td {
    border-bottom: 1.2em solid var(--surface-ground) !important;
  }

  /* OVERRIDE: Tabelle in Card → nutze Card-Surface */
  .sey-datatable--surface-card .elevated-row td,
  .sey-datatable--surface-card .elevated-row-start td,
  .sey-datatable--surface-card .p-datatable-row-group-header ~ .p-datatable-row-group-header td {
    border-top: 0.6em solid var(--p-toolbar-background) !important;
  }

  .sey-datatable--surface-card .elevated-row-end td {
    border-bottom: 0.6em solid var(--p-toolbar-background) !important;
  }
  .sey-datatable--surface-card .elevated-row-end-before-expanded + tr > td {
    border-bottom: 1.2em solid var(--p-toolbar-background) !important;
  }

  .elevated-row-end-before-expanded.type-boolean + tr + tr:after {
    content: ' ';
    position: absolute;
    left: -1.5em;
    right: -1.5em;
    height: 1px;
    margin-top: -0.3em;
    background-color: var(--signalred-500);
  }

  .p-datatable-column-header-content .header-label-wrapper {
    width: 100%;
  }

  td.sey-dt-editable-cell {
    position: relative;
    padding: 0;
  }

  /* Small blue corner hint like Excel */
  tr.sey-dt-row--selected td.sey-dt-editable-cell::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 0;
    /* Triangle via borders */
    border-top: 6px solid var(--p-primary-color, #3b82f6);
    border-right: 6px solid transparent;
    pointer-events: none;
    opacity: 0.9; /* subtle but visible */
  }

  /* Default: no indicator */
  td.sey-dt-editable-cell::before {
    content: none;
  }

  /* Ensures the clickable hitarea spans the full cell content area */
  .sey-dt-cell-hitarea {
    width: 100%;
    height: 100%;
  }
}
</style>
